import React, {
    useCallback, useEffect, useMemo, useState, useContext
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';

import { ModalNextProvider, Icon, Loader, DateField } from '@jutro/components';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { ServiceManager } from '@jutro/services';
import { BreakpointTrackerContext } from '@jutro/layout';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { Currency as CurrencyField } from 'gw-components-platform-react';
import { getFontIcon } from '../../../utils/lobIconUtil';
import metadata from './BillingSummaryPage.metadata.json5';
import styles from './BillingSummaryPage.module.scss';
import messages from './BillingSummaryPage.messages';
import PageBannerComponent
    from '../../../../components/PageBannerComponent/PageBannerComponent';

const getTabHeader = (productCode, label = '', title) => {
    const icon = getFontIcon(productCode);
  
    return (
        <div id={`divFor${productCode}${label}`} className={styles.tabContent}>
            {productCode ? (
                <Icon id={`iconFor${productCode}${label}`} icon={icon} title={title.defaultMessage} area-label={title.defaultMessage} />
            ) : null}
            <span id={`labelFor${productCode}${label}`}>{label.toUpperCase()}</span>
        </div>
    );
};

const getCellPolicyType = (rowData) => {
    const lobCode = rowData.policyLOBCode;
    const icon = getFontIcon(lobCode);
    return <Icon role="img" icon={icon} title={lobCode} key={lobCode} />;
};

const getCellPolicyNumber = (rowData) => {
    return (
        <span>
            {rowData.policyNumber}
        </span>
    );
};

const getCellDueDate = (rowData, rowIndex) => {
    const dueDateValue = rowData.dueDate;
    if (!dueDateValue) {
        return (
            <span>
                -
            </span>
        );
    }
    return (
        <DateField
            id={`dueDateValue_${rowIndex}`}
            value={dueDateValue}
            format="short"
            dataType={_.isString(dueDateValue) ? 'string' : 'object'}
            readOnly
            hideLabel
            layout="full-width"
            showOptional={false}
        />
    );
};

const getCellAmount = (rowData, rowIndex) => {
    return (
        <CurrencyField
            id={`paidAmount_${rowIndex}`}
            value={rowData.totalValue}
            dataType="object"
            readOnly
            hideLabel
        />
    );
};



function BillingSummaryPage(props) {
    const { match, authHeader, history } = props;
    const { params } = match;
    const { streamId } = params;

    const localeService = ServiceManager.getService('locale-service');
    const defaultCurrencyCode = localeService.getDefaultCurrencyCode().toUpperCase();
    let pageRef = React.createRef();
    const [pageLoaded, setPageLoaded] = useState(false);

    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );

    const { PolicyService } = useDependencies('PolicyService');

    // screen states
    const [isLoadingData, updateIsLoadingData] = useState(true);
    const [selectedInvoiceStreamId, updateSelectedInvoiceStreamId] = useState(streamId);
    const [selectedCurrency, updateSelectedCurrency] = useState(defaultCurrencyCode);
    const [activeTab, updateActiveTab] = useState(selectedInvoiceStreamId);

    // fetched data
    const [accountBillingSummaryFromApi, setAccountBillingSummaryFromApi] = useState({});
    const [invoiceStreamDetailsMapFromApi, setInvoiceStreamDetailsMapFromApi] = useState(new Map());
    const [policySummariesFromApi, setPolicySummariesFromApi] = useState([]);
    const [upcomingPaymentDetails, setUpcomingPaymentDetails] = useState([]);

    const { accountLevelBilling = false, invoiceStreams = [] } = accountBillingSummaryFromApi;

    useEffect(() => {
        if (!pageLoaded && pageRef.current) {
            const element = ReactDOM.findDOMNode(pageRef.current).parentNode;
            window.setTimeout(() => {
                element.children.billingSummary.focus();
            }, 1000);
            setPageLoaded(true);
        }

    }, [pageRef]);

    const updateMap = useCallback(
        (invoiceStreamId) => (fetchData) => {
            setInvoiceStreamDetailsMapFromApi(
                invoiceStreamDetailsMapFromApi.set(invoiceStreamId, fetchData)
            );

            const { totalValue } = fetchData;
            const { currency } = totalValue;
            updateSelectedCurrency(currency.toUpperCase());
        },
        [invoiceStreamDetailsMapFromApi]
    );

    const fetchAccountLevelInvoiceStreams = useCallback(() => {
        let accountInvoicePromise;
        let policySummaryPromise;

        if (_.isEmpty(accountBillingSummaryFromApi)) {
            //accountInvoicePromise = BillingService.getAccountInvoiceStreamInformation(
            accountInvoicePromise = BillingService.getInvoiceStreamInformationForEffectiveUser_EY(
                authHeader
            ).then((accountInvoiceStreams) => {
                const {
                    accountLevelBilling: accountLevelBillingFromApi = false,
                    invoiceStreams: invoiceStreamsFromApi = []
                } = accountInvoiceStreams;

                let defaultInvoiceStreamId;
                if (invoiceStreamsFromApi.length > 0) {
                    defaultInvoiceStreamId = invoiceStreamsFromApi[0].internalId;
                }
                if (accountLevelBillingFromApi && invoiceStreamsFromApi.length === 1) {
                    defaultInvoiceStreamId = invoiceStreamsFromApi[0].internalId;
                    updateSelectedInvoiceStreamId(invoiceStreamsFromApi[0].internalId);
                    updateActiveTab(invoiceStreamsFromApi[0].internalId);
                }

                setAccountBillingSummaryFromApi(accountInvoiceStreams);
                return defaultInvoiceStreamId;
            });
        } else {
            accountInvoicePromise = Promise.resolve(accountBillingSummaryFromApi);
        }

        if (policySummariesFromApi.length === 0) {
            //policySummaryPromise = PolicyService.getAccountPolicySummaries(authHeader).then(
            policySummaryPromise = PolicyService.getUserPolicySummaries(authHeader).then(
                setPolicySummariesFromApi
            );
        } else {
            policySummaryPromise = Promise.resolve(policySummariesFromApi);
        }

        return Promise.all([accountInvoicePromise, policySummaryPromise]).catch(() => {
            ModalNextProvider.showAlert({
                title: messages.failedToLoadPolices,
                message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).then(() => {
                history.push('/home');
            }, _.noop);
        });
    }, [
        BillingService,
        PolicyService,
        history,
        authHeader,
        accountBillingSummaryFromApi,
        policySummariesFromApi
    ]);

    const checkIsAllBroker = useCallback(() => {
        let isAllBroker = false;

        if (policySummariesFromApi.length > 0) {
            let brokerPolicies = policySummariesFromApi.filter((policySummary) => {
                return policySummary?.producerContact?.isBroker;
            });
            if (brokerPolicies.length === policySummariesFromApi.length) {
                isAllBroker = true;
            }
        }
        return isAllBroker;
    }, [policySummariesFromApi]);

    const checkIsBrokerPolicy = ((policyId) => {
        let isBroker = false;

        if (policySummariesFromApi.length > 0) {
            let brokerPolicies = policySummariesFromApi.filter((policySummary) => {
                return policySummary?.producerContact?.isBroker && policySummary?.periods[0].policyId === policyId;
            });
            if (brokerPolicies.length > 0) {
                isBroker = true;
            }
        }
        return isBroker
    });


    const getPaymentDetailsGroupFromMap = useCallback(
        (invoiceStreamId) => {
            return invoiceStreamDetailsMapFromApi.get(invoiceStreamId);
        },
        [invoiceStreamDetailsMapFromApi]
    );


    const fetchAccountUpcomingPayments = useCallback(() => {
        let upcomingInvoicePromise;
        //let policySummaryPromise;

        if (_.isEmpty(upcomingPaymentDetails)) {
            upcomingInvoicePromise = BillingService.getUpcomingPaymentsInformation_EY(
                authHeader
            ).then((upcomingPayments) => {
                setUpcomingPaymentDetails(upcomingPayments);
            }).catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.failedToLoadPolices,
                    message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).then(() => {
                    history.push('/home');
                }, _.noop);
            });
        }
        return upcomingInvoicePromise;
    },
        [BillingService, authHeader]
    );


    const fetchInvoiceDetailsByStreamId = useCallback(
        (invoiceStreamId, forceRefresh = false) => {
            const invoiceDetails = getPaymentDetailsGroupFromMap(invoiceStreamId);
            let invoiceDetailsPromise;

            if ((invoiceStreamId && _.isEmpty(invoiceDetails)) || forceRefresh) {
                //invoiceDetailsPromise = BillingService.getInvoiceStreamDetails( 
                invoiceDetailsPromise = BillingService.getUserInvoiceStreamDetails_EY(
                    invoiceStreamId,
                    authHeader
                )
                    .then(updateMap(invoiceStreamId))
                    .catch(() => {
                        ModalNextProvider.showAlert({
                            title: messages.failedToLoadPolices,
                            message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.ok
                        }).then(() => {
                            history.push('/home');
                        }, _.noop);
                    });
            } else {
                invoiceDetailsPromise = Promise.resolve(invoiceDetails);
            }

            return invoiceDetailsPromise;
        },
        [getPaymentDetailsGroupFromMap, BillingService, authHeader, updateMap, history]
    );

    const selectedLobCode = useMemo(() => {
        const foundInvoiceStreams = invoiceStreams.filter(
            (item) => item.internalId === selectedInvoiceStreamId
        );
        return foundInvoiceStreams.length > 0 ? foundInvoiceStreams[0].lobcode : undefined;
    }, [invoiceStreams, selectedInvoiceStreamId]);

    const onTabClick = useCallback(
        (invoiceStreamIdFromTabId) => {
            fetchInvoiceDetailsByStreamId(invoiceStreamIdFromTabId).finally(() => {
                updateActiveTab(invoiceStreamIdFromTabId);
                updateSelectedInvoiceStreamId(invoiceStreamIdFromTabId);
            });
        },
        [fetchInvoiceDetailsByStreamId]
    );

    const reloadInvoiceDetails = useCallback(
        (invoiceStreamId) => {
            updateIsLoadingData(true);
            return fetchInvoiceDetailsByStreamId(invoiceStreamId, true).finally(() => {
                updateIsLoadingData(false);
            });
        },
        [fetchInvoiceDetailsByStreamId]
    );

    useEffect(() => {
        updateIsLoadingData(true);
        fetchAccountLevelInvoiceStreams().then((retValues) => {
            if (retValues) {
                const initStreamId = selectedInvoiceStreamId || retValues[0];
                fetchInvoiceDetailsByStreamId(initStreamId).finally(() => {
                    onTabClick(initStreamId);
                    updateIsLoadingData(false);
                });
            }
        });
        fetchAccountUpcomingPayments();
        // execure once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const noData = useCallback(
        () => {
            return invoiceStreams.length === 0;
        },
        [invoiceStreams]
    );

    const getBackButton = () => {
        const breakpoint = useContext(BreakpointTrackerContext);
        return breakpoint === 'desktop' ? 'Home' : ''
    };

    const generateBillingDetailsOverrides = useCallback(() => {
        return {
            productTypeColumn: { onCell: getCellPolicyType },
            policyNumberColumn: { onCell: getCellPolicyNumber },
            amountColumn: { onCell: getCellAmount },
            dueDateColumn: { onCell: getCellDueDate },

        };
    }, [upcomingPaymentDetails]);

    if (isLoadingData) {
        return <Loader loaded={!isLoadingData} />;
    }

    const overrideProps = {

        /* noDataMessageContainer: {
            visible: noData(),
            message: messages.noBillingData
        }, */
        billingSummaryContainer: {
            visible: !noData(),
        },
        backButton: {
            content: getBackButton()
        },
        noPaymentSchedulesTitle: {
            visible: noData(),
        },
        billingTabsId: {
            content: _.map(invoiceStreams, (item) => {
                return {
                    id: item.internalId,
                    type: 'element',
                    component: 'tab',
                    componentProps: {
                        heading: getTabHeader(item.lobcode, item.label,messages[item.lobcode])
                    },
                    content: [
                        {
                            id: `billingContentFor${item.internalId}`,
                            type: 'container',
                            component: 'BillingSummary',
                            componentProps: {
                                accountLevelBilling,
                                invoiceStreamId: selectedInvoiceStreamId,
                                lobCode: selectedLobCode,
                                paymentGroupDetails: getPaymentDetailsGroupFromMap(
                                    selectedInvoiceStreamId
                                ),
                                currency: selectedCurrency,
                                policySummaries: policySummariesFromApi,
                                reloadInvoiceDetails: reloadInvoiceDetails,
                                isBroker: checkIsBrokerPolicy(item.label)
                            }
                        }
                    ]
                };
            }),
            activeTab: activeTab,
            onTabChange: onTabClick
        },
        upcomingPaymentTable: {
            data: upcomingPaymentDetails,
            visible: !_.isEmpty(upcomingPaymentDetails) && !checkIsBrokerPolicy()
        },
        brokerPaymentInfo: {
            visible: checkIsBrokerPolicy()
        },
        noUpcomingPaymentTitle: {
            visible: _.isEmpty(upcomingPaymentDetails) && !checkIsBrokerPolicy()
        },
        ...generateBillingDetailsOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            pagebannercomponent: PageBannerComponent
        }
    };

    return <div><div id="billingSummary" role="navigation"  tabindex="0" ref={pageRef}></div>{renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}</div>;

}

BillingSummaryPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            streamId: PropTypes.string
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({}).isRequired
};

export default withAuthenticationContext(withRouter(BillingSummaryPage));
